import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { List, X } from "@phosphor-icons/react";

const NAV_LINKS = [
    { label: "Services", href: "#services" },
    { label: "Portfolio", href: "#portfolio" },
    { label: "Nos réalisations", href: "#showreel" },
    { label: "Approche", href: "#approche" },
    { label: "Processus", href: "#processus" },
    { label: "FAQ", href: "#faq" },
    { label: "Journal", href: "/blog", internal: true },
    { label: "Contact", href: "#contact" },
];

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_brand-builders-43/artifacts/6hieg182_Artboard%2010.svg";

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-[#faf7f1]/85 border-b border-scd"
                    : "bg-transparent"
            }`}
            data-testid="site-header"
        >
            <div className="px-6 md:px-12 lg:px-16 py-4 flex items-center justify-between">
                <a
                    href="#top"
                    className="flex items-center gap-3"
                    data-testid="header-logo-link"
                >
                    <img
                        src={LOGO_URL}
                        alt="Studio Cent Douze"
                        className="h-9 md:h-10 w-auto"
                    />
                </a>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV_LINKS.map((l) =>
                        l.internal ? (
                            <Link
                                key={l.href}
                                to={l.href}
                                data-testid={`nav-${l.label.toLowerCase()}-link`}
                                className="hover-underline label-eyebrow text-cream/80 hover:text-cream transition-colors"
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-${l.label.toLowerCase()}-link`}
                                className="hover-underline label-eyebrow text-cream/80 hover:text-cream transition-colors"
                            >
                                {l.label}
                            </a>
                        ),
                    )}
                </nav>

                <a
                    href="#contact"
                    data-testid="header-cta-button"
                    className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-yellow text-[#272727] font-semibold text-sm hover:bg-green hover:text-[#faf7f1] transition-colors"
                >
                    Démarrer un projet
                    <span aria-hidden>→</span>
                </a>

                <button
                    aria-label="Menu"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden text-cream p-2"
                >
                    {open ? <X size={26} weight="bold" /> : <List size={26} weight="bold" />}
                </button>
            </div>

            {open && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="lg:hidden border-t border-scd bg-scd"
                    data-testid="mobile-nav-menu"
                >
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV_LINKS.map((l) =>
                            l.internal ? (
                                <Link
                                    key={l.href}
                                    to={l.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                                    className="font-display text-3xl text-cream"
                                >
                                    {l.label}
                                </Link>
                            ) : (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                                    className="font-display text-3xl text-cream"
                                >
                                    {l.label}
                                </a>
                            ),
                        )}
                        <a
                            href="#contact"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-cta-button"
                            className="mt-4 inline-flex items-center justify-center px-5 py-3 rounded-full bg-yellow text-[#272727] font-semibold"
                        >
                            Démarrer un projet →
                        </a>
                    </div>
                </motion.div>
            )}
        </motion.header>
    );
}
